/**
 * An asset's logo, with a monogram fallback.
 *
 * The path is derived rather than carried through the API. `syncLogos` writes
 * every file to `/logos/<kind>/<SYMBOL>.png` using this same sanitisation, so
 * the client can compute it from data it already has — which means the vault,
 * the history list and the live feed all get logos without joining any of them
 * back to the asset table.
 *
 * The cost of deriving it is that a missing file 404s rather than being known
 * to be absent up front, so every logo carries a fallback and one of them will
 * fire (1INCH has no logo at either source today).
 */
import { useState } from 'react'

import type { Rarity } from '../lib/api'

/** Mirrors the filename sanitisation in `apps/api/scripts/logos.ts`. */
export function logoPath(kind: string, symbol: string): string {
  const safe = symbol.replace(/[^A-Za-z0-9._-]/g, '_').toUpperCase()
  return `/logos/${kind}/${safe}.png`
}

export function AssetLogo({
  kind,
  symbol,
  size = 40,
  rarity,
  className = '',
}: {
  kind: string
  symbol: string
  size?: number
  /** Tints the ring and the fallback. Omit for neutral chrome. */
  rarity?: Rarity
  className?: string
}) {
  const [failed, setFailed] = useState(false)
  const tint = rarity ? 'var(--rarity)' : 'var(--color-faint)'

  return (
    <span
      className={`relative grid shrink-0 place-items-center overflow-hidden ${className}`}
      style={{
        width: size,
        height: size,
        // A flat plate behind the mark: these come from a dozen different
        // sources at a dozen different levels of padding, and without a
        // consistent ground they read as a jumble on the card.
        background: 'var(--color-surface-3)',
        border: `1px solid color-mix(in srgb, ${tint} 30%, transparent)`,
      }}
    >
      {failed ? (
        <span
          className="font-bold"
          style={{ fontSize: Math.max(9, size * 0.3), color: tint, letterSpacing: '0.02em' }}
        >
          {symbol.slice(0, 4)}
        </span>
      ) : (
        <img
          src={logoPath(kind, symbol)}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="size-full object-contain"
          style={{ padding: Math.round(size * 0.12) }}
        />
      )}
    </span>
  )
}
